import { sentenceCase } from "../../../constants";
import { getCategoryNameById } from "../../../utils";

/** Maps a wallet transaction type to the categories API group used to resolve its category_id. */
const CATEGORY_GROUP_BY_TYPE = {
  recharge: "recharge",
  bundle_recharge: "recharge",
  bill: "bill_payment",
  voucher_payment: "vouchers",
  transfer: "send_money",
};

/** Only these transaction types have a detail view; anything else should not open this component. */
export const SUPPORTED_TRANSACTION_TYPES = [
  "transfer",
  "withdraw",
  "deposit",
  "recharge",
  "bundle_recharge",
  "bill",
  "voucher_payment",
  "commission",
  "refund"
];

const maskedSuffix = (value) => (value ? ` - ${value}` : "");

const formatReason = (value) =>
  value ? sentenceCase(String(value).replace(/_/g, " ")) : null;

/** Builds the type-specific display config for TransactionDetail; returns null for unsupported types. */
export const getTransactionTypeConfig = (transaction, I18n) => {
  const {
    id,
    fee,
    type,
    amount,
    reason,
    customer_name,
    from_bank,
    commission,
    offer_name,
    created_at,
    account_bank,
    voucher_name,
    bill_company,
    account_title,
    receiver_bank,
    sender_account,
    account_number,
    receiver_account,
    telco_display_name,
    consumer_number_masked,
    consumer_number,
    customer_phone,
    logo_link,
    coins_earned,
    discount,
    total_amount,
    purpose,
    category_id,
    details,
    sender_name,
    receiver_name,
    customer
  } = transaction || {};

  if (!SUPPORTED_TRANSACTION_TYPES.includes(type)) return null;

  const customerStr = customer || customer_phone;

  const consumerNumber = consumer_number_masked || consumer_number || customer_phone;
  const categoryGroup = CATEGORY_GROUP_BY_TYPE[type];
  const categoryName = categoryGroup ? getCategoryNameById(categoryGroup, category_id) : null;
  const base = {
    id,
    createdAt: created_at,
    amount,
    commission,
    coinsEarned: coins_earned,
    fee,
    discount,
    totalAmount: discount != null ? (Math.abs(amount || 0) - discount) : null,
    reasonLabel: formatReason(reason) || categoryName || formatReason(purpose),
    logo: logo_link,
    telco: telco_display_name,
    showCoins: ["recharge", "bundle_recharge", "bill", "voucher_payment"].includes(type),
  };

  switch (type) {
    case "deposit":
      return {
        ...base,
        label: I18n.money_received || "Money received",
        name: sender_name || account_title || "-",
        bankLine: `${account_bank || from_bank || "Rupin"}${maskedSuffix(account_number || sender_account)}`,
      };

    case "transfer":
    case "withdraw":
      return {
        ...base,
        label: I18n.money_sent || "Money sent",
        name: receiver_name || account_title || "-",
        bankLine: `${account_bank || receiver_bank || "Rupin"}${maskedSuffix(account_number || receiver_account)}`,
      };

    case "recharge":
      return {
        ...base,
        label: I18n.easyload || "Easyload",
        name: customer_name || "-",
        bankLine: `${telco_display_name || "-"}${maskedSuffix(customerStr)}`,
      };

    case "bundle_recharge":
      return {
        ...base,
        label: I18n.bundle_recharge || "Easyload package",
        name: customer_name || "-",
        bankLine: `${telco_display_name || "-"}${maskedSuffix(customerStr)}`,
        details,
      };

    case "bill":
      return {
        ...base,
        label: I18n.bill_payment || "Bill payment",
        name: customer_name || "-",
        bankLine: `${bill_company || "-"}${maskedSuffix(consumerNumber)}`,
      };

    case "voucher_payment":
      return {
        ...base,
        label: I18n.voucher || "Voucher",
        name: customer_name || "-",
        bankLine: `${voucher_name || "-"}${maskedSuffix(consumerNumber)}`,
      };

    case "commission":
      return {
        ...base,
        fee: 0,
        label: I18n.cash_back || "Cashback",
      };

    default:
      return {
        ...base,
        name: sentenceCase(type) || 'Transaction',
        bankLine: account_bank ? `${account_bank} *${account_number ?? ''}` : ''
      }
  }
};
